/**
 * Applies the bundle discount code to the cart once the cart qualifies.
 *
 * The code is a manual discount, so Liquid cannot see its value or its
 * eligibility rules until it is actually on the cart. The theme's older paths
 * only ever appended ?discount= to the checkout URL, which reaches checkout but
 * leaves the cart object empty — so nothing rendered in the cart or drawer.
 * This puts the code on the cart itself via the Ajax cart API's `discount`
 * parameter; Shopify evaluates the minimum, the customer segment and the usage
 * limits, and the theme renders cart.cart_level_discount_applications.
 *
 * Does no arithmetic and hardcodes no amount. If Shopify declines the code,
 * nothing is displayed and nothing is charged differently.
 */
(function () {
  'use strict';

  var config = window.cartDiscountConfig;
  if (!config || !config.enabled || !config.code) return;

  var CODE = config.code;
  var REFUSED_KEY = 'cartDiscountRefusedFor:' + CODE.toUpperCase();

  // The regions Liquid renders a cart-level discount into. Line prices are not
  // among them: an order-level discount is not in final_line_price, so only the
  // totals change when one lands.
  var REGIONS = ['.r-side-cart .min-cart-footer', '.cart-footer-right'];

  var inFlight = false;

  // Shopify declines for reasons that hold until the cart changes (ineligible
  // customer, under the minimum, code exhausted). Remember which cart we were
  // refused on so we retry when the cart moves rather than on every page.
  // sessionStorage rather than a variable so a refusal survives navigation.
  function refusedFor() {
    try { return sessionStorage.getItem(REFUSED_KEY); } catch (e) { return null; }
  }

  function rememberRefusal(signature) {
    try { sessionStorage.setItem(REFUSED_KEY, signature); } catch (e) {}
  }

  // Item count and total, not line keys: Liquid and the Ajax cart hash the same
  // line to different keys (41366070329481:7974e008... vs :f84b7a8c...), so a
  // key-based signature never agrees between the two paths.
  function signatureOf(cart) {
    return cart.item_count + ':' + cart.total_price;
  }

  function hasSubscription(cart) {
    return cart.items.some(function (item) {
      return item.selling_plan_allocation != null;
    });
  }

  // /cart.js returns each discount application flat, while /cart/update.js
  // wraps it in an allocation ({ amount, discount_application }). Same list,
  // two shapes — reading only the flat one makes a successful apply look like a
  // decline, because the title comes back undefined.
  function isApplied(cart) {
    return (cart.cart_level_discount_applications || []).some(function (entry) {
      var app = entry.discount_application || entry;
      return (app.title || '').toUpperCase() === CODE.toUpperCase();
    });
  }

  /**
   * Show the discount by swapping in the regions Liquid just rendered for it.
   *
   * Not a reload: on the cart page that costs a full document load on top of
   * the requests already spent, so the customer sits looking at a cart with no
   * discount and then watches the page jump. Not Shopify.onSuccess either —
   * that routes into sucss(), which rebuilds the entire drawer and reopens it,
   * flashing the contents and flinging a closed drawer open over the page.
   *
   * Swapping the rendered region keeps Liquid as the single source of the
   * markup, leaves the order-note textarea and the checkout form element
   * untouched, and works while the drawer is shut — so the totals are already
   * right when the customer next opens it.
   */
  function render() {
    var targets = REGIONS.map(function (selector) {
      return { selector: selector, node: document.querySelector(selector) };
    }).filter(function (target) {
      return target.node;
    });

    if (!targets.length) return;

    fetch('/cart', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        targets.forEach(function (target) {
          var fresh = doc.querySelector(target.selector);
          if (fresh) target.node.innerHTML = fresh.innerHTML;
        });
      })
      .catch(function (error) {
        console.warn('TEST:: cart discount applied but totals not refreshed', error);
      });
  }

  function post(signature) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: CODE })
    })
      .then(function (r) { return r.json(); })
      .then(function (updated) {
        // Shopify answers 200 with the cart unchanged when it declines the
        // code, so success is "is it on the cart now", not the status code.
        if (isApplied(updated)) {
          render();
        } else {
          rememberRefusal(signature);
        }
      });
  }

  function settle() {
    inFlight = false;
  }

  function failed(error) {
    inFlight = false;
    console.warn('TEST:: cart discount could not be applied', error);
  }

  /**
   * Page load. Liquid has already checked the preconditions against the cart it
   * rendered, so this goes straight to the POST — no /cart.js lookup, and no
   * request at all on the pages that do not qualify.
   */
  function applyFromLiquid() {
    if (!config.needsApply || inFlight) return;
    if (refusedFor() === config.signature) return;

    inFlight = true;
    post(config.signature).then(settle, failed);
  }

  /**
   * After a cart mutation. Liquid's snapshot is stale by now, so the cart has
   * to be re-read before deciding.
   */
  function applyFromCart() {
    if (inFlight) return;
    inFlight = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var signature = signatureOf(cart);

        if (isApplied(cart) || !hasSubscription(cart) || refusedFor() === signature) {
          return;
        }
        return post(signature);
      })
      .then(settle, failed);
  }

  // Covers the bundle flow, which navigates to /cart rather than using Ajax.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyFromLiquid);
  } else {
    applyFromLiquid();
  }

  // Covers drawer adds and quantity changes. jQuery is deferred on the bundle
  // templates, hence the guard. Our own /cart/update.js is a native fetch, so
  // it never lands here and cannot retrigger this.
  if (window.jQuery) {
    jQuery(document).ajaxComplete(function (event, xhr, settings) {
      var url = (settings && settings.url) || '';
      if (url.indexOf('/cart/') !== -1) setTimeout(applyFromCart, 200);
    });
  }
})();
